import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { ConfirmDialog } from '@/shared/components/custom/confirm-dialog/ConfirmDialog';
import { useToast } from '@/shared/hooks/useToast';
import { useConfirm } from '@/shared/hooks/useConfirm';
import { useDebouncedCallback } from '@/shared/hooks/useDebouncedCallback';
import { OrganizationUnitFormDialog } from '../OrganizationUnitFormDialog';
import { useDeleteOrganizationUnit } from '../../hooks/useDeleteOrganizationUnit';
import { useOrganizationUnits } from '../../hooks/useOrganizationUnits';

interface OrgAreaNodeActionsProps {
  areaId: string;
  childAreaCount: number;
}

export function OrgAreaNodeActions({ areaId, childAreaCount }: OrgAreaNodeActionsProps) {
  const { t } = useTranslation('organizationUnits');
  const { toast } = useToast();
  const { confirm, isOpen, options, handleConfirm, handleCancel } = useConfirm();
  const deleteUnit = useDeleteOrganizationUnit();
  const [editOpen, setEditOpen] = useState(false);
  const [addChildOpen, setAddChildOpen] = useState(false);

  const { data: units = [] } = useOrganizationUnits();
  const unit = units.find((u) => u.id === areaId);

  const handleDelete = useDebouncedCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!unit) return;

    const hasWarning = childAreaCount > 0 || unit.memberCount > 0;
    const result = await confirm({
      title: t('deleteDialog.title', { name: unit.name }),
      description: hasWarning
        ? t('deleteDialog.warningDescription', {
            name: unit.name,
            childCount: childAreaCount,
            memberCount: unit.memberCount,
          })
        : t('deleteDialog.description', { name: unit.name }),
      confirmLabel: hasWarning ? t('deleteDialog.forceDelete') : t('deleteDialog.delete'),
      cancelLabel: t('deleteDialog.cancel'),
      variant: 'destructive',
    });

    if (!result.confirmed) return;

    try {
      await deleteUnit.mutateAsync(unit.id);
      toast.success({ title: t('toast.deleted') });
    } catch {
      // global handler shows error toast
    }
  });

  return (
    <>
      <div
        className="flex items-center gap-0.5"
        data-testid={`org-chart-area-actions-${areaId}`}
        onPointerDown={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              disabled={deleteUnit.isPending}
              onClick={(e) => {
                e.stopPropagation();
                setAddChildOpen(true);
              }}
              aria-label={t('actions.addChild')}
              className="h-6 w-6 text-muted-foreground hover:text-foreground"
              data-testid={`org-chart-area-add-child-${areaId}`}
            >
              <Plus className="h-3 w-3" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">{t('actions.addChild')}</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              disabled={deleteUnit.isPending || !unit}
              onClick={(e) => {
                e.stopPropagation();
                setEditOpen(true);
              }}
              aria-label={t('actions.edit')}
              className="h-6 w-6 text-muted-foreground hover:text-foreground"
              data-testid={`org-chart-area-edit-${areaId}`}
            >
              <Pencil className="h-3 w-3" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">{t('actions.edit')}</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              disabled={deleteUnit.isPending || !unit}
              onClick={handleDelete}
              aria-label={t('actions.delete')}
              className="h-6 w-6 text-muted-foreground hover:text-destructive"
              data-testid={`org-chart-area-delete-${areaId}`}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">{t('actions.delete')}</TooltipContent>
        </Tooltip>
      </div>

      {unit && (
        <>
          <OrganizationUnitFormDialog open={editOpen} onOpenChange={setEditOpen} unit={unit} />
          <OrganizationUnitFormDialog
            open={addChildOpen}
            onOpenChange={setAddChildOpen}
            parentId={unit.id}
          />
        </>
      )}

      {isOpen && options && (
        <ConfirmDialog
          open={isOpen}
          onOpenChange={(open) => !open && handleCancel()}
          title={options.title}
          description={options.description}
          confirmLabel={options.confirmLabel}
          cancelLabel={options.cancelLabel}
          variant={options.variant}
          onConfirm={handleConfirm}
        />
      )}
    </>
  );
}
